import React from "react";
import styled from "styled-components";

import {
  FlexColumnContainer,
  FlexRowContainer,
  useWindowDimensions,
} from "@trailhead/core-ui";
import { Size, Tag, breakpoints, colors } from "@trailhead/asurion-ui";

import WomanOnPhone from "../assets/woman-on-phone.png";

const SmartSetupLayoutContainer = styled(FlexRowContainer)`
  justify-content: center;
`;

const SmartSetupLayoutContentContainer = styled(FlexColumnContainer)`
  background: ${colors.neutralBrightest};
  position: relative;
  width: 100vw;
  margin: 0 -100vw;
  justify-content: center;
  align-items: center;
`;

const SmartSetupBanner = styled.div`
  overflow: hidden;
  width: 100%;
  height: 10rem;

  ${breakpoints.md} {
    height: 28.125rem;
  }
`;

const ImageWrapper = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.28);
  object-position: 1.375rem 25%;

  ${breakpoints.md} {
    transform: scale(1.3);
    object-position: 2.1875rem 20%;
  }

  ${breakpoints.lg} {
    transform: scale(1.2);
    object-position: 4.375rem 35%;
  }
`;

const SmartSetupMainContainer = styled(FlexColumnContainer)`
  width: 100%;
  max-width: 80rem;

  ${breakpoints.lg} {
    position: relative;
    flex-direction: row;
    justify-content: space-between;
  }

`;

const SmartSetupContentContainer = styled(FlexColumnContainer)`
  position: relative;

  & > div:last-child {
    background: ${colors.white};
    border-radius: 0.5rem;
    position: relative;
    top: -0.5rem;
  }

  ${breakpoints.md} {
    top: -3.5rem;
    margin: 0 1rem;
  }

  ${breakpoints.lg} {
    top: -12rem;
    flex: 1 1 50%;
    max-width: fit-content;
    align-self: flex-start;
  }
`;

const SmartSetupCTAContainer = styled(FlexColumnContainer) <{
  offsetCta: boolean;
}>`
  position: relative;
  align-self: center;
  align-items: center;

  ${breakpoints.lg} {
    // 2 values only since for now we only support 2 CTAs , might need to pass margin-top value
    // instead of offsetCta prop when there's gonna be more in the future
    ${({ offsetCta }) =>
    offsetCta ? "margin-top: 16em;" : "margin-top: 12em;"}
    top: -12em;
    align-self: unset;
    align-items: unset;
    flex: 0 1 30%;
  }
`;

const SmartSetupCTAWrapper = styled.div`
  ${breakpoints.lg} {
    position: sticky;
    top: 18em;
  }
`;

const TagWrapper = styled(Tag)`
  font-size: 1.25rem;
  padding: 0.5rem 1rem 1rem;
  font-weight: 700;
  border-radius: 0.5rem;
  position: absolute;
  top: -2.75rem;
`;

interface SmartSetupLayoutProps {
  content: JSX.Element | null;
  cta: JSX.Element | null;
  offsetCta?: boolean;
  canHideCta?: boolean;
}

export const SmartSetupLayout: React.FC<SmartSetupLayoutProps> = ({
  content = null,
  cta = null,
  offsetCta = false,
  canHideCta = false,
}) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= Size.MD;
  const shouldHideCTA = !isDesktop && canHideCta;

  return (
    <SmartSetupLayoutContainer>
      <SmartSetupLayoutContentContainer>
        <SmartSetupBanner>
          <ImageWrapper alt="woman-on-phone" src={WomanOnPhone} />
        </SmartSetupBanner>
        <SmartSetupMainContainer>
          <SmartSetupContentContainer>
            <TagWrapper size="regular" text="Get started now" type="dark" />
            <div>
              {content}
            </div>
          </SmartSetupContentContainer>
          {!shouldHideCTA ? (
            <SmartSetupCTAContainer offsetCta={offsetCta}>
              <SmartSetupCTAWrapper>{cta}</SmartSetupCTAWrapper>
            </SmartSetupCTAContainer>
          ) : null}
        </SmartSetupMainContainer>
      </SmartSetupLayoutContentContainer>
    </SmartSetupLayoutContainer>
  );
};
